<script lang="ts" setup>
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import { cursorPagerStoreFactory } from "@/stores/factory/list-store-factory";
import { readCoinVotePage } from "@/apis/coinRank";
import ThreadList, {
  ThreadListInstance,
} from "@/components/mobile/ThreadList.vue";
import { useCoinRank } from "@/stores/coinRank";
import { CoinBalanceRecordEntityType, CoinVote } from "@/types/coin";
import { useRoute } from "vue-router";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useStoryBook } from "@/stores/story-book";
import { useMate } from "@/stores/mate";
import { useGalGameHome } from "@/stores/galGameHome";
import { getRankServerDtStart, useRankText } from "@/composable/rank";
import {
  ValidRankEntityType,
  ValidRankType,
  VoteTargetType,
} from "@/composable/rank/rankConfig";
import { ApiGetCollection } from "@/apis/collection";
import { CoinRank } from "@/types/coinRank";

const coinRankStore = useCoinRank();
const route = useRoute();
const coinVoteMapStore = entityStoreFactory<CoinVote>("id");
const storyBookStore = useStoryBook();
const mateStore = useMate();
const gameStore = useGalGameHome();
const { updateMap } = coinVoteMapStore;
const rankEntityType = ref<ValidRankEntityType | undefined>();
const rankEntity = ref<VoteTargetType>();
const voteList = ref<ThreadListInstance | null>(null);
const rankId = ref<number>(
  route.query.id ? Number(route.query.id) : coinRankStore.currentCoinRank!.id
);
const loadingEntity = ref(false);
const { type } = route.query;
const isCurrentRank = computed(
  () => rankId.value === coinRankStore.currentCoinRank!.id
);
const currentRank = computed<CoinRank>(
  () => coinRankStore.getRankById(rankId.value)!
);
const currentRankServerDtStart = computed(() =>
  getRankServerDtStart(currentRank.value.dtStart)
);

const cursorEncoder = (entity: CoinVote, pointsToNextItems: boolean) => {
  return btoa(
    JSON.stringify({
      id: entity.id,
      isGolden: entity.isGolden,
      _pointsToNextItems: pointsToNextItems,
    })
  );
};
const { dataList, initListIfAbsent, fetchOlderList } =
  cursorPagerStoreFactory<CoinVote>({
    entityStore: coinVoteMapStore,
    fetch: async (cursor) => {
      const response = await readCoinVotePage({
        filter: {
          coinRankId: rankId.value,
          entityType: rankEntityType.value,
          entityId: rankEntity?.value?.id,
          isGolden: type === "golden" ? 1 : undefined,
        },
        cursor,
        sort: { field: "id", order: "desc" },
      });
      updateMap(response.page.data);
      return response.page.data;
    },
    cursorEncoder,
  });

// Lifecycle
const loading = ref(false);
onBeforeMount(async () => {
  loading.value = true;
  try {
    await coinRankStore.getOrFetchHistoryCoinRank();
  } finally {
    loading.value = false;
  }
});

watch(
  () => route.query,
  async () => {
    const { type, uuid, id } = route.query as {
      type: ValidRankType;
      uuid: string;
      id: string;
    };
    const queryRankId = Number(id);
    if (route.name !== "rankVoteList" || !type || !uuid) {
      return;
    }
    try {
      loadingEntity.value = true;
      if (queryRankId) {
        rankId.value = queryRankId;
      }
      if (type === "story") {
        rankEntityType.value = CoinBalanceRecordEntityType.StoryBook;
        rankEntity.value = await storyBookStore.getOrFetchStoryItem(
          uuid as string
        );
      } else if (type === "mate") {
        rankEntityType.value = CoinBalanceRecordEntityType.Mate;
        rankEntity.value = await mateStore.getOrFetchMate(uuid as string);
      } else if (type === "game") {
        rankEntityType.value = CoinBalanceRecordEntityType.Game;
        rankEntity.value = await gameStore.getOrFetchGame(uuid as string);
      } else if (type === "collection") {
        rankEntityType.value = CoinBalanceRecordEntityType.Collection;
        const { item } = await ApiGetCollection(uuid as string);
        rankEntity.value = item;
      }
      voteList.value?.resetScrollLoadingState();
    } finally {
      loadingEntity.value = false;
    }
  },
  { immediate: true }
);
</script>
<template>
  <div class="w-full h-full grid grid-rows-[auto_1fr]">
    <threads-nav show-back>
      <template #center>
        <div>
          <span v-if="!loading">
            {{
              isCurrentRank
                ? $t("rank.currentLeaderboard")
                : $t("rank.historyLeaderboard")
            }}-{{ `${useRankText(currentRank)}` }}
          </span>
        </div>
      </template>
    </threads-nav>
    <div class="h-full overflow-hidden">
      <div
        v-if="loadingEntity || loading"
        class="h-full w-full flex items-center justify-center min-h-[200px]"
      >
        <span class="loading loading-sm" />
      </div>
      <thread-list
        v-else
        ref="voteList"
        :on-pull-load="() => initListIfAbsent(true)"
        :on-scroll-load="fetchOlderList"
      >
        <div v-if="rankEntity && rankEntityType">
          <rank-banner-of-entity
            :id="rankId"
            :entity="rankEntity"
            :entity-type="rankEntityType"
          />
          <rank-status-of-entity
            :item="rankEntity"
            :type="rankEntityType"
            class="mx-2"
          />
        </div>
        <div v-else class="overflow-hidden rounded">
          <rank-banner :dt-start="currentRankServerDtStart" />
        </div>
        <sticky-nav class="bg-base-100">
          <div class="text-lg">
            {{ $t("Coin.voteRecords") }}
          </div>
        </sticky-nav>
        <div class="flex flex-col gap-2 p-4 pt-0">
          <rank-vote-item
            v-for="item in dataList"
            :key="item.id"
            :coin-vote="item"
          />
        </div>
      </thread-list>
    </div>
  </div>
</template>
